<template>
  <ez-form
    :formKey="formKey"
    :loading-key="loadingKey"
    :additional-headers="additionalHeaders"
    :action="action"
    :method="method"
    :submitType="submitType"
    :additionalData="{
      ...additionalData,
      ...(!inventoryEnabled ? { inventoryEnabled: inventoryEnabled?.toString() } : {}),
      ...(!recipeManagementEnabled ? { recipeManagementEnabled: recipeManagementEnabled?.toString() } : {}),
    }"
    @success="onSubmitSuccess"
    @error="onSubmitError"
    ref="venueForm"
    class="venue-form"
  >
    <slot name="prepend" />

    <ez-input
      :formKey="formKey"
      :value="name"
      :placeholder="`${$t('global.venue')} Name`"
      name="name"
      @onChange="updateFormState('name', $event)"
      :label="`${$t('global.venue')} name*`"
    />

    <ez-input
      :formKey="formKey"
      :value="venue.legalName"
      name="legalName"
      label="Legal Business Name"
      placeholder="Enter Your Legal Business Name"
      class="mt-12"
    />

    <ez-input
      :formKey="formKey"
      :value="venue.companyRegistrationNumber"
      name="companyRegistrationNumber"
      label="Company Registration Number / Tax ID"
      placeholder="Enter Your Company Registration Number / Tax ID"
      class="mt-12"
    />

    <ez-input
      :formKey="formKey"
      :value="customInfo"
      name="customInfo"
      label="Additional Info"
      placeholder="Additional Info"
      class="mt-12"
    />

    <slot name="primaryContact" />
    <hr class="address-separator" />
    <legend>Invoices</legend>

    <ez-drop-down
      v-if="isAdmin"
      :data="venueSubscription"
      :form-key="formKey"
      is-full-width
      name="pendingInvoicesSubscription"
      label="Subscription"
      :selected="venue.pendingInvoicesSubscription"
      @change="onSubscriptionyDropdownChange"
      class="mt-12"
    />

    <ez-input
      v-if="isAdmin && !isUnlimited && !isNone"
      :key="`limit-${pendingInvoicesLimit}`"
      :formKey="formKey"
      :value="pendingInvoicesLimit"
      :disabled="isStripe"
      name="pendingInvoicesLimit"
      label="Limit"
      placeholder="Limit"
      class="mt-12"
      type="number"
    />

    <ez-input
      v-if="isAdmin && !isUnlimited && !isNone"
      :key="`remaining-${pendingInvoicesRemaining}`"
      :formKey="formKey"
      :value="pendingInvoicesRemaining"
      name="pendingInvoicesRemaining"
      label="Remaining"
      placeholder="Remaining"
      class="mt-12"
      type="number"
    />

    <hr class="address-separator" />

    <legend>Features</legend>

    <div class="label mt-12" v-if="isAdmin">
      Inventory Enabled
      <ez-on-off
        class="ml-8"
        :form-key="formKey"
        :value="inventoryEnabled?.toString()"
        :checked="inventoryEnabled"
        @change="onInventoryEnabledChange"
        name="inventoryEnabled"
      />
    </div>

    <p class="help-block__text mb-0" v-if="isAdmin">
      Manage inventory and conduct stocktake for products and ingredients.
    </p>

    <div class="label mt-12" v-if="isAdmin">
      Recipe Management
      <ez-on-off
        class="ml-8"
        :form-key="formKey"
        :value="recipeManagementEnabled?.toString()"
        :checked="recipeManagementEnabled"
        @change="onRecipeManagementEnabledChange"
        name="recipeManagementEnabled"
      />
    </div>

    <p class="help-block__text mb-0" v-if="isAdmin">
      Manage ingredients, recipes and menu items and their margins.
    </p>

    <hr class="address-separator" />

    <ez-address-form
      ref="deliveryAddress"
      :formKey="formKey"
      :fieldValidators="requiredAddressFields"
      :address="venue.shippingAddress || {}"
      name="shippingAddress"
    >
      <template #legend>Delivery Address</template>
    </ez-address-form>

    <ez-checkbox
      :formKey="formKey"
      :checked="billingAddressEnabled"
      class="billing-checkbox"
      name="notification"
      label="Billing Address same as Delivery Address"
      value="BILLING_ADDRESS"
      @change="value => (billingAddressEnabled = value)"
    />

    <ez-address-form
      ref="billingAddress"
      v-if="!billingAddressEnabled"
      :fieldValidators="requiredAddressFields"
      :formKey="formKey"
      :address="venue.billingAddress || {}"
      class="billing-address-form"
      name="billingAddress"
    >
      <template #legend>Billing Address</template>
    </ez-address-form>

    <hr class="address-separator" />

    <h4>Working Hours</h4>
    <v-select-search
      v-if="timeZones.length"
      class="width-100"
      align-left
      is-full-width
      name="timezone"
      label="Time Zone"
      :data="timeZones"
      value-property="name"
      placeholder="Select Time Zone"
      searchPlaceholder="Search Time Zone"
      :selected="selectedTimeZone"
      :has-clear="false"
    />

    <ez-working-hours
      ref="workingHours"
      :form-key="formKey"
      name-from="workingHours[from]"
      name-to="workingHours[to]"
      :value-from="workingHours.from || ''"
      :value-to="workingHours.to || ''"
      class="working-hours"
    />
    <ez-input
      :formKey="formKey"
      placeholder="Instructions"
      name="instruction"
      :value="venue.instruction"
      label="Delivery Instructions"
      class="instruction-input"
    />

    <div class="form-help">
      <h4>Stripe</h4>
    </div>
    <div class="stripe-input">
      <ez-input
        :form-key="formKey"
        :value="venue.stripeCustomerId"
        name="stripeCustomerId"
        placeholder="Enter Stripe Customer ID"
        label="Stripe Customer ID"
      />
      <a
        v-if="venue.stripeUrl"
        :href="venue.stripeUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="stripe-input__link"
      >
      <font-awesome-icon icon="link" class="link-icon" />
      </a>
    </div>
    <slot name="prependBottom" />

    <template #buttons>
      <slot name="buttons"></slot>
    </template>
  </ez-form>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import { isAdmin, LOADING_KEY, venueSubscription } from '@/util/constants';
import { falsy } from '@/util/utils';
import EzWorkingHours from '@/components/ui/WorkingHours';
import EzCheckbox from '@/components/ui/Checkbox';
import EzInput from '@/components/ui/Input';
import EzForm from '@/components/ui/Form';
import EzAddressForm from '@/views/common/address';
import VSelectSearch from '@/components/v3/patterns/VSelectSearch';
import EzDropDown from '@/components/ui/Dropdown';
import EzOnOff from '@/components/ui/OnOff/EzOnOff.vue';

export default {
  components: {
    EzWorkingHours,
    EzCheckbox,
    EzInput,
    EzForm,
    EzAddressForm,
    VSelectSearch,
    EzDropDown,
    EzOnOff,
  },
  props: {
    loadingKey: {
      required: false,
      type: String,
    },
    formKey: {
      type: String,
      required: true,
    },
    action: {
      type: String,
      required: true,
    },
    method: {
      type: String,
      required: true,
    },
    venueId: {
      type: Number,
      required: false,
      default: -1,
    },
    venueData: {
      type: Object,
      required: true,
    },
    additionalData: {
      type: Object,
      default: () => {},
    },
    additionalHeaders: {
      type: Object,
      required: false,
      default: () => {},
    },
    submitType: {
      type: String,
      default: 'urlencoded',
    },
    prefilledName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      billingAddressEnabled: true,
      formValid: false,
      timeZones: [],
      fetchedVenue: null,
      requiredValues: {
        name: null,
      },
      billingAddressRequiredValues: {},
      copyOfRequiredValues: {},
      venueSubscription,
      isUnlimited: false,
      isNone: false,
      isStripe: false,
      inventoryEnabled: false,
      recipeManagementEnabled: false,
      currentSubscription: null,
      pendingInvoicesLimit: null,
      pendingInvoicesRemaining: null,
    };
  },
  computed: {
    ...mapState('entities/users', ['loggedUser']),
    venue() {
      return this.venueData;
    },
    name() {
      return this.venue.name ? this.venue.name : this.prefilledName;
    },
    customInfo() {
      return this.venue.customInfo;
    },
    workingHours() {
      return this.venue.workingHours || {};
    },
    isAdmin() {
      return isAdmin();
    },
    requiredAddressFields() {
      return {};
    },
    selectedTimeZone() {
      return (
        this.venue?.timezone ||
        (!this.isAdmin
          ? (this.loggedUser?.venues && this.loggedUser?.venues[0]?.timezone) ||
            this.loggedUser?.distributor?.timezone
          : this.timeZones.find(zone => zone.name === 'Asia/Singapore').name)
      );
    },
    isBillingChecked() {
      return this.venue.billingAddress ? Object.keys(this.venue.billingAddress).length > 0 : false;
    },
  },
  watch: {
    venue(value) {
      this.billingAddressEnabled = !value.billingAddress;
      this.isUnlimited = value.pendingInvoicesSubscription === 'unlimited';
      this.isNone = value.pendingInvoicesSubscription === 'none';
      this.inventoryEnabled = value.inventoryEnabled;
      this.recipeManagementEnabled = value.recipeManagementEnabled;
      this.currentSubscription = this.venue.pendingInvoicesSubscription;
      this.isStripe = this.currentSubscription === 'stripe';
      this.pendingInvoicesLimit = this.venue.pendingInvoicesLimit;
      this.pendingInvoicesRemaining = this.venue.pendingInvoicesRemaining;
    },
    async billingAddressEnabled(val) {
      if (!val) {
        this.copyOfRequiredValues = {
          ...this.copyOfRequiredValues,
          ...this.billingAddressRequiredValues,
        };
        if (this.venueId !== -1) await this.initialFormState();
      } else {
        this.copyOfRequiredValues = this.requiredValues;
        if (this.venueId !== -1) await this.initialFormState();
      }
      this.validateForm();
    },
    formValid() {
      this.$emit('formValid', this.formValid);
    },
  },
  methods: {
    ...mapMutations('loading', ['SET_LOADING', 'CLEAR_LOADING']),
    resetForm() {
      this.$refs.venueForm.reset();
      this.$refs.deliveryAddress.reset();
      // eslint-disable-next-line no-unused-expressions
      !this.billingAddressEnabled && this.$refs.billingAddress.reset();
      this.$refs.workingHours.reset();
    },
    // eslint-disable-next-line consistent-return
    updateFormState(name, value) {
      this.copyOfRequiredValues[name] = value;
      this.validateForm();
    },
    async initialFormState() {
      await this.$nextTick();

      this.copyOfRequiredValues = {
        name: this.name,
        deliveryAddress: (this.venue.shippingAddress || {}).street,
        country: (this.venue.shippingAddress || {}).country,
        city: (this.venue.shippingAddress || {}).city,
        ...(!this.billingAddressEnabled
          ? {
              billingAddress: (this.venue.billingAddress || {}).street,
              billingCity: (this.venue.billingAddress || {}).city,
              billingCountry: (this.venue.billingAddress || {}).country,
            }
          : {}),
      };
    },
    onSubscriptionyDropdownChange(val) {
      const previous = this.currentSubscription;
      const next = val.id;

      if (next === 'stripe') {
        // limit always comes from Stripe
        this.pendingInvoicesLimit = this.venue.stripeInvoicesLimit ?? null;

        // remaining only when coming from unlimited / none
        if (previous === 'unlimited' || previous === 'none') {
          this.pendingInvoicesRemaining = this.venue.stripeInvoicesRemaining ?? null;
        }
      }

      this.currentSubscription = next;
      this.isUnlimited = next === 'unlimited';
      this.isNone = next === 'none';
      this.isStripe = next === 'stripe';
    },
    validateForm() {
      this.formValid = true;
      Object.keys(this.copyOfRequiredValues).forEach(key => {
        if (falsy(this.copyOfRequiredValues[key])) this.formValid = false;
      });
    },
    submitForm() {
      this.$refs.venueForm.onSubmit();
    },
    onSubmitSuccess(response) {
      this.$emit('submitSuccess', response);
      if (!this.venue.id || this.venue.id === -1) this.resetForm();
    },
    onSubmitError(response) {
      this.$emit('submitError', response);
    },
    ...mapActions('entities/distributors', ['fetchTimeZones']),
    ...mapActions('entities/users', ['adminFetchCurrencies', 'fetchCurrencies']),
    async setTimeZones() {
      this.timeZones = await this.fetchTimeZones();
    },
    onInventoryEnabledChange(ev) {
      this.inventoryEnabled = ev;
    },
    onRecipeManagementEnabledChange(ev) {
      this.recipeManagementEnabled = ev;
    },

  },
  async created() {
    this.SET_LOADING({ [LOADING_KEY.JOKER]: true });

    if (this.venueId !== -1) {
      await this.initialFormState();
      this.validateForm();
    } else {
      this.requiredValues.name = this.prefilledName;
      this.copyOfRequiredValues = this.requiredValues;
    }
    await this.setTimeZones();

    this.billingAddressEnabled = !this.isBillingChecked;
    this.CLEAR_LOADING({ key: LOADING_KEY.JOKER });

    this.inventoryEnabled = this.venue.inventoryEnabled;
    this.recipeManagementEnabled = this.venue.recipeManagementEnabled;
    this.currentSubscription = this.venue.pendingInvoicesSubscription;
    this.isUnlimited = this.currentSubscription === 'unlimited';
    this.isNone = this.currentSubscription === 'none';
    this.isStripe = this.currentSubscription === 'stripe';
    this.pendingInvoicesLimit = this.venue.pendingInvoicesLimit;
    this.pendingInvoicesRemaining = this.venue.pendingInvoicesRemaining;
  },
};
</script>

<style scoped lang="scss">
:deep() .select-search {
  .select-search__list {
    max-height: 280px;
  }

  .select-search__item {
    height: 42px;
    display: flex;
    align-items: center;
  }
}

.venue-form {
  max-width: 26rem;
  .ez-image-upload {
    margin-bottom: 1rem;
  }
  > .input-group + .input-group {
    margin-top: 0.75rem;
  }
  .ez-contact {
    margin-top: 0.75rem;
  }
  .address-separator,
  .name-separator {
    border: 0;
    border-top: 1px dashed #dee1e4;
    margin: 1.25rem 0;
  }
  .billing-checkbox {
    margin-top: 1rem;
    :deep() .input-group__label {
      color: #6c7995;
    }
  }
  .billing-address-form {
    margin-top: 1.5rem;
  }
  h4 {
    @include font-size(14px);
    font-weight: 400;
    padding: 0;
    margin-bottom: 16px;
  }
  .instruction-input {
    margin-top: 20px;
  }
  .working-hours {
    margin-top: 20px;
  }
}

.label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 500;
}

.stripe-input {
  display: flex;
  align-items: flex-end; // line the icon up with the field, not the label
  gap: 8px;              // the "small margin"

  .input-group {
    flex: 1 1 auto;
    min-width: 0;        // lets it shrink instead of overflowing the 26rem form
  }

  &__link {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 36px;        // match your input height so the icon sits centered on it
    color: inherit;
    text-decoration: none;
  }

  .link-icon {
    color: $color-primary-blue;
  }
}
</style>
